/**
 * "Has this build already reported X today?" — the thing that keeps usage
 * counting a FEATURE count rather than a usage-intensity signal.
 *
 * Why it exists: the server stores one row per (day, feature) with no
 * identifier, so without a client-side cap a single person opening a panel
 * forty times would read on the dashboard exactly like forty people opening it
 * once. Marking each feature once per UTC day makes the number mean "builds
 * that touched this feature today", which is the question being asked.
 *
 * Why this is not an identifier: what is stored is a FEATURE KEY and a DATE.
 * There is no id, no counter and no value derived from the machine — two
 * installs that used the same feature on the same day store byte-identical
 * marks, so nothing here can single anyone out or be correlated across
 * installs. That is the same shape as the update check's existing once-a-day
 * cache key, and the reason both are lawful without a consent prompt where a
 * persisted UUID was not (see `purgeLegacyTelemetryKeys` in src/index.js).
 *
 * Storage may be unavailable (private windows, cleared site data, a hardened
 * webview). Every access is guarded: on failure the mark is treated as unset,
 * which at worst reports a feature twice in a day. Losing a mark must never
 * throw into a caller — a counter is not worth a broken panel.
 */

const PREFIX = 'apiboo.usage.day.';

/** Today in UTC as YYYY-MM-DD — the server buckets by UTC day, so the client must too. */
const utcDay = () => new Date().toISOString().slice(0, 10);

/**
 * Claim today's mark for `key`.
 *
 * @returns {boolean} true when this call claimed it (the caller should report),
 *   false when it was already claimed today (the caller should stay silent).
 */
export function claimDaily(key) {
  const today = utcDay();
  try {
    const name = PREFIX + key;
    if (window.localStorage.getItem(name) === today) return false;
    window.localStorage.setItem(name, today);
    return true;
  } catch {
    // No storage: report. Over-reporting is a worse number; throwing is a worse app.
    return true;
  }
}

/**
 * Give today's mark for each of `keys` back, so the next use reports again.
 *
 * Called when a batch failed to reach the server. Without it a single failed
 * POST — a plane, a captive portal, a corporate proxy that blocks the API —
 * would lose those features for the rest of the UTC day, which systematically
 * under-counts exactly the offline and locked-down users worth hearing about.
 */
export function releaseDaily(keys) {
  try {
    keys.forEach((key) => window.localStorage.removeItem(PREFIX + key));
  } catch {
    // storage unavailable: the mark was never written either
  }
}

/**
 * Drop every mark this module owns — used when usage counting is switched off,
 * so turning it back on does not stay silent for the rest of the day.
 */
export function clearDailyMarks() {
  try {
    const { localStorage } = window;
    const doomed = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const name = localStorage.key(i);
      if (name && name.startsWith(PREFIX)) doomed.push(name);
    }
    doomed.forEach((name) => localStorage.removeItem(name));
  } catch {
    // nothing to clean up if storage cannot be read
  }
}
